import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  CardMedia,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteUserApi } from "../../api/users/usersApi";
import emptyUsersImg from "../../assets/empty-users-img.png";
import TableLoader from "../../components/loader/tableLoader";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import CustomTable from "../../components/table/CustomTable";
import TableAction from "../../components/table/tableAction";
import { showSnackbar } from "../../redux/actions/commonAction";
import { getAllUsers } from "../../redux/actions/usersAction";
import CustomDrawer from "./../../components/drawer/CustomDrawer";
import ManageUsers from "./manageUsers";
import { useStyles } from "./styles";
import { Refresh } from "@mui/icons-material";

const Users = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { allUsers } = useSelector((state) => state.users);
  const { globalLoader } = useSelector((state) => state.common);
  const [open, setOpen] = useState(false);
  const [showDialog, setDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
    //eslint-disable-next-line
  }, []);

  const fetchData = () => {
    dispatch(getAllUsers());
  };

  const columns = [
    {
      field: "firstName",
      label: "User",
      render: (row) => <Box>{`${row.firstName} ${row.lastName}`}</Box>,
    },
    { field: "email", label: "Email", type: "email", align: "left" },
    {
      field: "role",
      label: "Role",
      align: "left",
      render: (row) => (
        <Box display="flex">
          <AvatarGroup>
            {row.roles
              ? row.roles.map((data) => {
                  return (
                    <Tooltip title={data}>
                      <Avatar
                        sx={{
                          bgcolor: (theme) =>
                            theme.palette[
                              data === "admin" ? "secondary" : "info"
                            ].main,
                        }}
                      >
                        {data.charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  );
                })
              : "-"}
          </AvatarGroup>
        </Box>
      ),
    },
    {
      field: "accounts",
      label: "Account Numbers",
      align: "left",
      render: (row) => (
        <>
          {row?.accounts?.length > 0 ? (
            <Box display="flex" alignItems="center">
              <Typography>{row?.accounts[0]}</Typography>
              {row?.accounts?.length > 1 && (
                <Tooltip title={row?.accounts.slice(1).join("\n")}>
                  <Typography className={classes.linkText}>
                    +{row?.accounts?.length - 1}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          ) : (
            ""
          )}
        </>
      ),
    },
    {
      field: "supervisorName",
      label: "Supervisor",
      align: "left",
      render: (row) => (
        <Tooltip title={row?.supervisorEmail}>
          {row?.supervisorName ?? "-"}
        </Tooltip>
      ),
    },
    {
      label: "Actions",
      render: (rowData) => (
        <TableAction
          onEdit={() => handleAction(rowData)}
          onDelete={() => handleAction(rowData, false)}
        />
      ),
    },
  ];

  const handleClose = () => {
    setOpen(false);
    setDialog(false);
    setDeleting(false);
    setSelectedUser(null);
  };

  const handleAction = (user, isEdit = true) => {
    const userData = {
      ...user,
      fullName: `${user?.firstName} ${user?.lastName}`,
    };
    setSelectedUser(userData);
    if (isEdit) {
      setOpen(true);
    } else {
      setDialog(true);
    }
  };

  const onDeleteConfirm = async () => {
    setDeleting(true);
    let response = await deleteUserApi(selectedUser?.email);
    if (response) {
      handleClose();
      dispatch(getAllUsers());
      dispatch(
        showSnackbar({
          message: `${selectedUser?.fullName} Deleted Successfully`,
          severity: "success",
          open: true,
        })
      );
    }
  };

  const renderEmptyScreen = () => {
    return globalLoader ? (
      <TableLoader />
    ) : (
      <Box
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        mt={10}
      >
        <CardMedia
          component="img"
          alt="My Image"
          image={emptyUsersImg}
          sx={{
            maxWidth: "380px",
            width: "auto",
          }}
        />
        <Typography mt={2} mb={2}>
          Hello! Click the button to create a new user
        </Typography>
        <Button
          startIcon={<PersonAddAltIcon />}
          variant="contained"
          onClick={() => setOpen(true)}
        >
          Create User
        </Button>
      </Box>
    );
  };

  return (
    <>
      {allUsers?.length > 0 ? (
        <>
          <Box
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" gutterBottom={true}>
              Users
            </Typography>
            <Box display="flex" sx={{ gridGap: "12px" }}>
              <Button
                variant="outlined"
                onClick={() => fetchData()}
                startIcon={<Refresh />}
              >
                Refresh
              </Button>
              <Button
                startIcon={<PersonAddAltIcon />}
                variant="contained"
                onClick={() => setOpen(true)}
              >
                Create User
              </Button>
            </Box>
          </Box>
          <CustomTable columns={columns} data={allUsers} />
        </>
      ) : (
        renderEmptyScreen()
      )}
      <CustomDrawer
        open={open}
        title={Boolean(selectedUser) ? "Update User" : "Create New User"}
        onClose={handleClose}
      >
        <ManageUsers
          onClose={handleClose}
          edit={Boolean(selectedUser)}
          defaultValue={selectedUser}
        />
      </CustomDrawer>
      <ConfirmDialog
        loading={deleting}
        open={showDialog}
        title="Delete Confirmation"
        type="error"
        content={`Are you sure you want to delete the user ${selectedUser?.fullName}?`}
        onClose={handleClose}
        onClik={onDeleteConfirm}
      />
    </>
  );
};

export default Users;
